import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	type AssistantOrbConfig,
	defaultOrbConfig,
} from "@/types/ai-assistant-orb";
import { fetchOrb, OrbApiError, resetOrb, saveOrb } from "./orb.api";

/**
 * Muat/simpan tampilan orb Jenna.
 *
 * `draft` adalah yang sedang disetel admin dan berubah seketika supaya
 * pratinjau terasa hidup; `saved` adalah yang benar-benar ada di server.
 * Keduanya dipisah agar tombol Simpan tahu kapan ada perubahan, dan agar
 * Batal bisa mengembalikan tanpa memanggil server.
 */
export function useAssistantOrb() {
	const queryClient = useQueryClient();
	const [saved, setSaved] = useState<AssistantOrbConfig>(defaultOrbConfig);
	const [draft, setDraft] = useState<AssistantOrbConfig>(defaultOrbConfig);
	const [loading, setLoading] = useState(true);
	const [unavailable, setUnavailable] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => {
		const controller = new AbortController();
		abortRef.current = controller;
		fetchOrb(controller.signal)
			.then((orb) => {
				setSaved(orb);
				setDraft(orb);
				setUnavailable(false);
			})
			.catch((err) => {
				if (err instanceof Error && err.name === "AbortError") return;
				if (err instanceof OrbApiError && err.status === 404)
					setUnavailable(true);
				else setError(err instanceof Error ? err.message : String(err));
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, []);

	/** Setelah tersimpan, status asisten di-refetch supaya FAB & header ikut berganti. */
	const apply = useCallback(
		async (action: () => Promise<AssistantOrbConfig>) => {
			setError(null);
			const next = await action();
			setSaved(next);
			setDraft(next);
			await queryClient.invalidateQueries({
				queryKey: ["assistant", "status"],
			});
			return next;
		},
		[queryClient],
	);

	return {
		draft,
		saved,
		loading,
		unavailable,
		error,
		setDraft,
		/** Buang perubahan yang belum disimpan. */
		revert: useCallback(() => setDraft(saved), [saved]),
		save: useCallback(() => apply(() => saveOrb(draft)), [apply, draft]),
		reset: useCallback(() => apply(resetOrb), [apply]),
	};
}

/** True bila draft berbeda dari yang tersimpan — penentu tombol Simpan aktif. */
export function orbHasChanges(
	draft: AssistantOrbConfig,
	saved: AssistantOrbConfig,
): boolean {
	return (
		draft.variant !== saved.variant ||
		draft.size !== saved.size ||
		draft.colorFrom !== saved.colorFrom ||
		draft.colorTo !== saved.colorTo ||
		draft.followTheme !== saved.followTheme ||
		draft.params.some((value, i) => value !== saved.params[i])
	);
}
